/**
 * Calendar colour coding.
 *
 * A coach tags a booking option with one of these, and every session booked
 * against it renders in that colour on the calendar.
 *
 * This list mirrors `CalendarColors::PALETTE` in the scheduling service, which
 * validates writes against its own copy. The backend pins these values in
 * `spec/models/calendar_colors_spec.rb` -- keep the two in step or saves start
 * 422ing.
 */
export interface CalendarColor {
  value: string;
  name: string;
}

export const CALENDAR_COLORS: CalendarColor[] = [
  { value: "#D45224", name: "Orange" },
  { value: "#E7AC52", name: "Amber" },
  { value: "#81C785", name: "Green" },
  { value: "#3C877B", name: "Teal" },
  { value: "#4599DF", name: "Blue" },
  { value: "#5F6BBA", name: "Indigo" },
  { value: "#8548A8", name: "Purple" },
  { value: "#C73361", name: "Pink" },
];

export const CALENDAR_COLOR_VALUES = CALENDAR_COLORS.map((c) => c.value);

export function isCalendarColor(value?: string | null): boolean {
  return !!value && CALENDAR_COLOR_VALUES.includes(value);
}

export function calendarColorName(value?: string | null): string | undefined {
  return CALENDAR_COLORS.find((c) => c.value === value)?.name;
}

/**
 * Normalizes whatever a form or the API hands us into something safe to send:
 * a palette colour, or null for "no colour". Anything unrecognized becomes null
 * rather than a 422 -- a colour is cosmetic and never worth failing a save over.
 */
export function toPayloadColor(value?: string | null): string | null {
  return isCalendarColor(value) ? (value as string) : null;
}

/**
 * The background shading a calendar entry gets once its booking option has a
 * colour: a pale fill, with the title and time tinted in the same hue.
 *
 * Derived in HSL rather than by laying the colour over white at an alpha:
 * alpha keeps each colour's own lightness, so amber came out visibly paler
 * than indigo. Pinning the lightness makes all eight read as one system.
 */
const FILL_LIGHTNESS = 94;
const FILL_MAX_SATURATION = 70;
const INK_LIGHTNESS = 28;
const INK_MAX_SATURATION = 55;
// 35 keeps every palette colour's muted text at >= 4.5:1 on its fill (WCAG AA
// for the 12-14px time and name lines); 46 dropped amber to under 3:1.
const MUTED_INK_LIGHTNESS = 35;
const MUTED_INK_MAX_SATURATION = 35;

function toHsl(color: string): { h: number, s: number, l: number } {
  const hex = color.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) {
    return { h: 0, s: 0, l: l * 100 };
  }

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;

  if (max === r) {
    h = 60 * (((g - b) / delta) % 6);
  } else if (max === g) {
    h = 60 * ((b - r) / delta + 2);
  } else {
    h = 60 * ((r - g) / delta + 4);
  }

  return { h: (h + 360) % 360, s: s * 100, l: l * 100 };
}

function restate(color: string, lightness: number, maxSaturation: number): string {
  const { h, s } = toHsl(color);

  return `hsl(${Math.round(h)}, ${Math.round(Math.min(s, maxSaturation))}%, ${lightness}%)`;
}

export interface EventTint {
  fill: string;
  ink: string;
  muted: string;
}

/**
 * The shading for an entry, or null to draw it exactly as before.
 *
 * Only a palette colour -- one a coach picked -- shades an entry. A session
 * whose option has no colour arrives with the old orange, and clinics, blocks
 * and external-calendar entries with their own colours; none of those are a
 * choice, so they keep the white card they always had.
 */
export function eventTint(color?: string | null): EventTint | null {
  const value = CALENDAR_COLOR_VALUES.find(
    (palette) => palette.toLowerCase() === color?.trim().toLowerCase(),
  );

  if (!value) {
    return null;
  }

  return {
    fill: restate(value, FILL_LIGHTNESS, FILL_MAX_SATURATION),
    ink: restate(value, INK_LIGHTNESS, INK_MAX_SATURATION),
    muted: restate(value, MUTED_INK_LIGHTNESS, MUTED_INK_MAX_SATURATION),
  };
}
